import { motion } from "framer-motion";
import { ArrowRight, PlayCircle, Sparkles } from "lucide-react";
import { HERO } from "@/constants/testIds";

const AI_IMG =
  "https://static.prod-images.emergentagent.com/jobs/7f7f59f9-b87b-4610-b385-48ba29eca9db/images/b62c9ad98716d98f507f7c866404ab9608f65310aeb45340fd5a13e93fe57341.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.6, ease: "easeOut" },
  }),
};

const stats = [
  { value: "99.9%", label: "System Uptime", dot: true },
  { value: "20+", label: "Years of Expertise" },
  { value: "100+", label: "Projects Delivered" },
  { value: "24/7", label: "Enterprise Support" },
];

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid={HERO.root}
      className="relative min-h-[100svh] flex items-center pt-24 lg:pt-28 overflow-hidden"
    >
      {/* Background atmosphere */}
      <div className="absolute inset-0 -z-10 bg-[#0B0F19]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-blue-600/20 rounded-full blur-3xl animate-blob" />
        <div className="absolute bottom-0 right-10 w-[360px] h-[360px] bg-cyan-500/15 rounded-full blur-3xl animate-blob" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        {/* LEFT — content */}
        <div className="lg:col-span-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={0}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border border-white/10 text-xs sm:text-sm text-slate-200 mb-6"
          >
            <Sparkles className="w-4 h-4 text-cyan-400" />
            Transforming Industries with AI-Powered IoT Solutions
          </motion.div>

          <motion.h1
            data-testid={HERO.headline}
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={1}
            className="font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-[2.4rem] xl:text-5xl leading-[1.1] xl:whitespace-nowrap"
          >
            Build Smarter Businesses with{" "}
            <span className="gradient-text-vivid">AI &amp; IoT</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={2}
            className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed"
          >
            We help organizations accelerate digital transformation through
            <span className="text-white"> AI Agents</span>,
            <span className="text-white"> Industrial IoT</span>,
            <span className="text-white"> Edge Computing</span>,
            cloud-native platforms, predictive analytics, and intelligent automation.
          </motion.p>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={3}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid={HERO.scheduleDemo}
              onClick={() => scrollTo("contact")}
              className="btn-primary group inline-flex items-center justify-center gap-2 text-white px-7 py-3.5 rounded-full font-semibold"
            >
              Schedule a Demo
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid={HERO.talkExpert}
              onClick={() => scrollTo("contact")}
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-white/15 text-white hover:bg-white/5 hover:border-cyan-400/40 transition"
            >
              <PlayCircle className="w-4 h-4 text-cyan-400" />
              Talk to an Expert
            </button>
          </motion.div>

          {/* Stats — one line on lg+, wraps gracefully on mobile */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="show"
            custom={4}
            className="mt-10 flex flex-wrap lg:flex-nowrap items-center gap-x-4 lg:gap-x-5 xl:gap-x-7 gap-y-3 text-xs text-slate-300"
          >
            {stats.map((s) => (
              <div key={s.label} className="flex items-center gap-1.5 whitespace-nowrap">
                {s.dot ? (
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-slow" />
                ) : (
                  <span className="w-1 h-1 rounded-full bg-cyan-400/60" />
                )}
                <span className="font-heading font-semibold text-white">{s.value}</span>
                <span className="text-slate-400">{s.label}</span>
              </div>
            ))}
          </motion.div>
        </div>

        {/* RIGHT — AI image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-4 relative hidden lg:block"
        >
          <div className="relative aspect-square w-full max-w-[520px] ml-auto">
            <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-tr from-blue-600/30 via-cyan-500/20 to-transparent blur-2xl" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 glow-ring">
              <img
                src={AI_IMG}
                alt="AI and IoT network visualization"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B0F19]/40 via-transparent to-cyan-500/10 pointer-events-none" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
